/**
 * detail 画面の 1 区画（大枠設計 §7 の表示パターン #1「detail 集約」の器）。
 *
 * §7 の detail は「本文・帰属・実装先・被参照・違反」を 1 画面に集約する。区画の見出しと
 * 枠を画面ごとに書くと、同じ意味の区画が画面によって見出しの階層も余白も変わり、利用者が
 * 「どこまでが 1 つのまとまりか」を毎回読み直すことになる。器を 1 つに固定して、画面側は
 * 中身だけを持つ。
 *
 * 区画は `section` + `aria-label` で出す。見出しだけの `div` にすると、支援技術から
 * 「集約された detail」ではなく 1 枚の長い本文に見え、区画単位で読み飛ばせなくなる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business detail 画面の区画（見出し・補助導線・本文）の枠を共通化する。
 * @graph-connects none
 */
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { ReactNode } from 'react';

/** `DetailSection` の props。 */
export interface DetailSectionProps {
  /** 区画の見出し。`aria-label` にも使う。 */
  title: string;
  /** 見出しの右に置く件数バッジや補助導線。 */
  action?: ReactNode;
  /** 区画の本文。 */
  children: ReactNode;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business detail 画面の 1 区画を、見出し・補助導線・本文の 3 層で描く。
 * @graph-connects none
 */
export function DetailSection({ title, action, children }: DetailSectionProps) {
  return (
    <section aria-label={title}>
      <Card padding="md">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-bold text-[var(--color-text-primary)]">{title}</h2>
          {action}
        </div>
        <div className="mt-3">{children}</div>
      </Card>
    </section>
  );
}
